import { useEffect, useRef } from "react";
import type { EventWithSeries, SeriesSessionSummary } from "@shared/schema";
import { WavyMark } from "@/components/ui/wavy-mark";
import { BlurFade } from "@/components/ui/blur-fade";

/* ----------------------------------------------------------------------------
 * "What you'll build" — the OnPrompt Studio design templates.
 *
 * WHAT THIS REPLACED. Four bespoke samples costing 4.1MB: two were live
 * <iframe>s loading complete HTML documents out of /design-workshop-iframes,
 * each with an autoplaying background video inside, and a third ran a ten-slide
 * JS carousel on a timer. Three nested documents and two video decoders.
 *
 * SIX CLIPS IN TWO COLUMNS, EVERY ONE MOVING, ON THE PAGE'S OWN MARGINS. No
 * names and no captions — the designs are shown as work rather than listed as a
 * catalogue. Each clip keeps an aria-label, which is the only place a template
 * is still named.
 *
 * Every cell renders at its source ratio, so nothing is cropped at all. See
 * COLUMNS below for the sizes, and for what the earlier bento versions cost.
 *
 * The short column closes with a coda: one line, the format list in the script
 * face, and the section's own CTA.
 *
 * The loops are 800w h264, 95-397KB each, and every one is seamless: the tail
 * is folded back over the head so it does not snap on repeat. Measured seams
 * 1.03-4.17 of 255, against a visible threshold near 8. The Collection needed a
 * search to get there — the full 16.8s clip seamed at 8.08 and weighed 973KB,
 * while a 7s window starting at 6.0s seams at 1.03 and weighs 247KB.
 *
 * THEY COST NOTHING UNTIL THEY ARE ON SCREEN. preload="none" means the browser
 * fetches a poster and no video bytes at all; one IntersectionObserver starts a
 * clip when its cell enters the viewport and pauses it when it leaves, so a
 * visitor who never scrolls here pays for none of it. prefers-reduced-motion
 * holds every clip on its poster.
 * -------------------------------------------------------------------------- */

const CANVAS = "#faf9f5";
const INK = "#141413";
/* The reskin's card radius. The base site caps cards at 4px (client/CLAUDE.md),
   but .ds-cowork supersedes that everywhere on this page — testimonials, the
   persona cards, the FAQ, the date cards and the accordion all render 10px, and
   the reskin even rewrites the site's own rounded-[4px] to 10px
   (cowork-ds.css). A 4px cell in that company reads as a square corner. */
const CARD_RADIUS = "10px";

const HAIRLINE = "#e6dfd8";
const PRIMARY = "var(--terra)";

const FONT_DISPLAY = "var(--font-display)";
const FONT_SANS = "var(--font-sans)";
const FONT_MONO = "var(--font-mono)";

const DIR = "/images/design-workshop/onprompt";

/* TWO COLUMNS OF DIFFERENT WIDTHS, THREE CLIPS IN EACH, NOTHING CROPPED.

   Every cell renders at its source ratio, so `cover` has nothing to trim — the
   crop tables that governed the earlier bento versions are gone, not improved.
   Size variation comes from the two column widths (56/44) and from the clips'
   own shapes inside them. At the page's 1280 content width:

       wide 56%   bakery    700x418 (1.67)
                  dandelion 700x472 (1.48)
                  spotlight 700x388 (1.81)
       narrow 44% portfolio  545x367 (1.48)
                  reel       545x353 (1.54)
                  glow       545x278 (1.96)

   Split so the columns land close in height rather than by theme: the wide
   column's three are the taller ratios. Columns are left to end ragged —
   levelling them would mean cropping again.

   Not a CSS grid: grid rows align to their tallest cell, which would put a hole
   under every short one. Two independent flex columns have no rows to align.

   `name` and `kind` survive only to build each clip's aria-label, the one place
   a template is still named now that the captions are gone. */
const COLUMNS = [
  {
    key: "wide",
    cards: [
      { slug: "bakery",    name: "Cinematic Video Hero", kind: "Video Hero",      w: 800, h: 478 },
      { slug: "dandelion", name: "The Collection",       kind: "Video Hero",      w: 800, h: 539 },
      { slug: "spotlight", name: "Spotlight Reveal",     kind: "Cursor Reveal",   w: 800, h: 443 },
    ],
  },
  {
    key: "narrow",
    cards: [
      { slug: "portfolio", name: "Creative Portfolio",   kind: "Video Hero",      w: 800, h: 539 },
      { slug: "reel",      name: "Project Reel",         kind: "Scroll Carousel", w: 800, h: 518 },
      { slug: "glow",      name: "Ambient Glow",         kind: "Cursor Glow",     w: 800, h: 408 },
    ],
  },
] as const;

function SectionEyebrow({ label }: { label: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 11,
          letterSpacing: "0.24em",
          textTransform: "uppercase",
          color: PRIMARY,
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <span style={{ height: 1, width: 120, background: PRIMARY, opacity: 0.5, display: "inline-block" }} />
      <span style={{ color: PRIMARY, width: 32, height: 20, display: "inline-flex", alignItems: "center", justifyContent: "center" }}>
        <WavyMark size={18} color={PRIMARY} />
      </span>
    </div>
  );
}

export function SamplesShowcaseSection({
  event: _event,
  sessions: _sessions,
}: {
  event?: EventWithSeries;
  sessions?: SeriesSessionSummary[];
}) {
  void _event;
  void _sessions;

  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const root = gridRef.current;
    if (!root) return;
    const vids = Array.from(root.querySelectorAll("video"));
    if (!vids.length) return;
    if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;

    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          const v = e.target as HTMLVideoElement;
          if (e.isIntersecting) {
            // play() can reject before any user gesture; the poster is already
            // showing, so a rejection is a no-op.
            void v.play().catch(() => {});
          } else if (!v.paused) {
            v.pause();
          }
        }
      },
      { rootMargin: "120px 0px", threshold: 0.15 },
    );
    vids.forEach((v) => io.observe(v));
    return () => io.disconnect();
  }, []);

  return (
    <section aria-label="What you'll build" id="samples" className="cdw-samp-section">
      <style>{`
        .cdw-samp-section { background: ${CANVAS}; color: ${INK}; padding: 120px 0 100px; position: relative; }
        /* No width of its own. The section sits inside container-content, so
           zero padding here lines its heading and its grid up with every other
           section's left edge. An earlier pass broke it out to 1600 and the
           whole block sat wider than the page around it. */
        .cdw-samp-wrap { max-width: none; margin: 0; padding: 0; }

        .cdw-samp-h {
          font-family: ${FONT_DISPLAY};
          font-weight: 500;
          font-size: clamp(38px, 5.2vw, 62px);
          line-height: 1.12;
          letter-spacing: -0.035em;
          margin: 0;
          text-wrap: balance;
        }
        /* Two flex columns at 56/44. See COLUMNS above for the cell sizes and
           for why this is not a grid.

           No callout line here — "Design, build and ship fast, with an
           instructor by your side" is the HEADLINE of #learn directly above. */
        /* stretch, not flex-start: the two columns end level. Left ragged, the
           narrow one ran 68px past the wide one and the section trailed off
           rather than closing. */
        .cdw-samp-cols {
          margin-top: 48px;
          display: flex;
          align-items: stretch;
          gap: 20px;
        }
        .cdw-samp-col {
          display: flex;
          flex-direction: column;
          gap: 20px;
          min-width: 0;
          /* Whichever column is naturally shorter distributes its slack into
             its own gaps rather than leaving a hole at the bottom. */
          justify-content: space-between;
        }
        /* Minus half the gap each. 56% + 44% + a 20px gap is 100% PLUS 20px,
           which pushed the right column 20px past the section's edge. */
        .cdw-samp-col-wide   { flex: 0 0 calc(56% - 10px); }
        .cdw-samp-col-narrow { flex: 0 0 calc(44% - 10px); }

        /* The coda: a room of its own in the gap the short column leaves.
           Filled rather than sitting on the paper, so it reads as the section's
           close instead of a caption, and takes the page's own dark ground. */
        .cdw-samp-coda {
          /* Grows to take up the remaining height of its column, so the panel
             finishes flush with the clip opposite it. */
          flex: 1 1 auto;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: clamp(28px, 2.6vw, 38px);
          border-radius: ${CARD_RADIUS};
          background: var(--indigo);
        }
        .cdw-samp-coda-line {
          font-family: ${FONT_DISPLAY};
          font-weight: 500;
          font-size: clamp(21px, 2vw, 30px);
          line-height: 1.24;
          letter-spacing: -0.02em;
          /* On-dark steps, same correction the closing and Before/After needed:
             --terra is the lead family's 400, sized for paper. */
          color: var(--ds-lead-900);
          margin: 0;
          text-wrap: balance;
        }
        /* The list is the script, not the mono: it is the one place in this
           section that names what you can make, and the hand reads as an aside
           rather than a spec line. No uppercase — every Crayonize letter is
           already cap-height, so text-transform only crowds it. */
        .cdw-samp-coda-list {
          font-family: var(--font-hand, 'Crayonize', cursive);
          font-weight: 700;
          font-size: clamp(16px, 1.5vw, 21px);
          letter-spacing: 0;
          line-height: 1.55;
          color: var(--ds-lead-800);
          margin: 14px 0 0;
        }
        /* The page's main CTA — btn-cta, so ds-drawn-buttons.ts gives it the
           drawn frame rather than the secondary's underline. The frame's box
           sits flush with the copy above it, which is what makes the panel
           read as one left-aligned column. */
        .cdw-samp-coda-cta {
          margin-top: 26px;
          display: inline-flex;
          align-items: center;
          /* The panel is a flex column, so a stretched button would fill its
             width and .btn's own justify-content:center would read as a
             centred CTA. Shrink-wrap it against the left edge instead. */
          align-self: flex-start;
        }
        /* The button's own colour rules carry more weight than a single class,
           so the on-dark steps need the section in the selector. .ch-btn.btn-cta
           is authored at the lead 300 — the panel's own colour, so both the
           label and the frame (which strokes currentColor) would vanish. */
        .cdw-samp-section .cdw-samp-coda .cdw-samp-coda-cta {
          color: var(--ds-lead-900) !important;
          /* A shared mobile rule centres .btn with auto side margins, which on
             a phone put the button 27px in from the line it sits under. */
          margin-left: 0 !important;
          margin-right: auto !important;
        }
        /* Hover fills the frame and flips the label to the panel's ground, so
           the whole gesture stays inside the one hue. */
        .cdw-samp-section .cdw-samp-coda .cdw-samp-coda-cta:hover .ds-fill,
        .cdw-samp-section .cdw-samp-coda .cdw-samp-coda-cta:focus-visible .ds-fill {
          fill: var(--ds-lead-800) !important;
        }
        .cdw-samp-section .cdw-samp-coda .cdw-samp-coda-cta:hover,
        .cdw-samp-section .cdw-samp-coda .cdw-samp-coda-cta:focus-visible {
          color: var(--ds-lead-300) !important;
        }

        .cdw-samp-cell {
          position: relative;
          margin: 0;
          overflow: hidden;
          border-radius: ${CARD_RADIUS};
          border: 1px solid ${HAIRLINE};
          /* Only ever visible in the instant before a poster paints. */
          background: #14151a;
        }
        .cdw-samp-shot {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          /* Nothing to fit: the cell IS the clip's ratio. */
          object-fit: cover;
          display: block;
        }
        /* Below the desktop breakpoint the uneven columns stop helping — at
           26% of a tablet a clip is too small to read. Each column becomes a
           full-width two-up, then a single stack on a phone. */
        @media (max-width: 1024px) {
          .cdw-samp-cols { flex-direction: column; gap: 20px; }
          .cdw-samp-col,
          .cdw-samp-col-wide,
          .cdw-samp-col-narrow {
            flex: 1 1 auto;
            width: 100%;
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            align-items: start;
          }
        }
        @media (max-width: 767px) {
          .cdw-samp-section { padding: 88px 0 72px; }
          .cdw-samp-wrap { padding: 0 20px; }
          .cdw-samp-col,
          .cdw-samp-col-wide,
          .cdw-samp-col-narrow { grid-template-columns: 1fr; }
        }
      `}</style>

      <div className="cdw-samp-wrap">
        <BlurFade inView delay={0} duration={0.5} yOffset={12}>
          {/* The eyebrow said "What you'll build" directly above a heading that
              already says it. Two labels, one idea — the heading keeps it. */}
          <h2 className="cdw-samp-h">
            Designs like these.
            <br />
            Shipped by lunch.
          </h2>
        </BlurFade>

        <div className="cdw-samp-cols" ref={gridRef}>
          {COLUMNS.map((col) => (
            <div key={col.key} className={`cdw-samp-col cdw-samp-col-${col.key}`}>
              {col.cards.map((d) => (
                <figure key={d.slug} className="cdw-samp-cell" style={{ aspectRatio: `${d.w} / ${d.h}` }}>
                  <video
                    className="cdw-samp-shot"
                    poster={`${DIR}/${d.slug}-800.webp`}
                    width={d.w}
                    height={d.h}
                    muted
                    loop
                    playsInline
                    preload="none"
                    aria-label={`${d.name}, a ${d.kind.toLowerCase()} page template`}
                  >
                    <source src={`${DIR}/${d.slug}.mp4`} type="video/mp4" />
                  </video>
                </figure>
              ))}
              {col.key === "narrow" && (
                <div className="cdw-samp-coda">
                  <p className="cdw-samp-coda-line">One workflow. Design anything.</p>
                  <p className="cdw-samp-coda-list">
                    Websites · Landing pages · Apps · Emails · Social · Decks · Ads · Print
                  </p>
                  <a href="#tickets" className="btn btn-cta btn-lg ch-btn cdw-samp-coda-cta">
                    Reserve your seat
                  </a>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
